import { useState } from 'react';
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogCloseTrigger,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { 
  Text, 
  Stack, 
  Button, 
  Input, 
  Textarea,
  Box
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster';
import useWorkspace from '@/lib/hooks/use-workspace';
import getSportName from '@/utils/get-sport-name';
import api from '@/utils/api';

interface ManualFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: string;
  contentTypeId: string;
  onSuccess?: () => void;
}

interface FormData {
  name: string;
  description: string;
}

export default function ManualFormModal({
  isOpen,
  onClose,
  workspaceId,
  contentTypeId,
  onSuccess,
}: ManualFormModalProps) {
  const { workspace } = useWorkspace();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    const trimmedName = formData.name.trim();
    const trimmedDescription = formData.description.trim();
    
    if (!trimmedName || trimmedName.length < 2) {
      toaster.create({
        title: 'Name is required and must be at least 2 characters',
        type: 'error',
      });
      return false;
    }
    
    if (!trimmedDescription || trimmedDescription.length < 10) {
      toaster.create({
        title: 'Description is required and must be at least 10 characters',
        type: 'error',
      });
      return false;
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post('/ai-content-requests', {
        workspace_id: parseInt(workspaceId),
        content_type_id: parseInt(contentTypeId),
        name: formData.name.trim(),
        description: formData.description.trim(),
        country: workspace?.country || '',
        sport_type: getSportName(workspace?.sport_type_id || 0),
        assistant_name: 'genai',
      });

      toaster.create({
        title: 'AI Content Request created successfully',
        type: 'success',
      });

      onSuccess?.();
      handleClose();
    } catch (err: any) {
      toaster.create({
        title: err.response?.data?.error || 'Failed to create AI content request',
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({ name: '', description: '' });
      onClose();
    }
  };

  const getFieldValidation = () => {
    const trimmedName = formData.name.trim();
    const trimmedDescription = formData.description.trim();
    
    return {
      name: {
        isInvalid: !trimmedName || trimmedName.length < 2,
        errorText: !trimmedName ? 'Name is required' : trimmedName.length < 2 ? 'Name must be at least 2 characters' : ''
      },
      description: {
        isInvalid: !trimmedDescription || trimmedDescription.length < 10,
        errorText: !trimmedDescription ? 'Description is required' : trimmedDescription.length < 10 ? 'Description must be at least 10 characters' : ''
      }
    };
  };

  const validation = getFieldValidation();
  const hasValidationErrors = validation.name.isInvalid || validation.description.isInvalid;

  return (
    <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && handleClose()}>
      <DialogContent maxW="600px">
        <DialogHeader>
          <DialogTitle>Create Manual AI Content Request</DialogTitle>
          <DialogCloseTrigger />
        </DialogHeader>
        
        <DialogBody>
          <Text color="fg.muted" mb={6}>
            Create a new AI content request by providing a name and description.
          </Text>
          
          <Stack gap={4}>
            <Field 
              label="Name" 
              required 
              errorText={validation.name.errorText}
              helperText={`${formData.name.trim().length}/2 characters minimum`}
            >
              <Input
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Enter content name (min 2 characters)"
                disabled={isSubmitting}
              />
            </Field>
            
            <Field 
              label="Description" 
              required
              errorText={validation.description.errorText}
              helperText={`${formData.description.trim().length}/10 characters minimum`}
            >
              <Textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Enter content description (min 10 characters)"
                rows={4}
                disabled={isSubmitting}
              />
            </Field>
          </Stack>
          
          {hasValidationErrors && (
            <Box mt={4} p={3} bg="danger.subtle" borderRadius="md" border="1px solid" borderColor="danger">
              <Text color="danger" fontSize="sm">
                Please fix the validation errors above. Names must be at least 2 characters and descriptions must be at least 10 characters.
              </Text>
            </Box>
          )}
        </DialogBody>
        
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          
          <Button 
            colorPalette="brand" 
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={hasValidationErrors}
          >
            Create AI Content Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
